/**
 * Created by Evžen Rejfíř on 22.07.2025.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import Relay from 'react-relay';
import moment from 'moment';
import { Button } from 'react-bootstrap';

import AppPanel from '../fw/AppPanel.react';
import { wrapNotFound } from '../app/NotFoundHandler.react';
import Editor from '../fw/Editor.react';
import FormFields from '../fw/FormFields.react';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import { TariffTypes } from '../../common/model-api/TariffType';
import { getLastOrganisationId } from '../app/LastOrganisationId';
import { displayError } from '../app/errorDisplayer';

const messages = defineMessages({
    title: {
        id: 'tariffEdit.title',
        defaultMessage: 'Editing Tariff'
    },
    titleAdd: {
        id: 'tariffAdd.title',
        defaultMessage: 'Adding Tariff'
    },
    titleView: {
        id: 'tariffView.title',
        defaultMessage: 'Viewing Tariff'
    },
    tariffDetails: {
        id: 'tariff.details',
        defaultMessage: 'Tariff Details'
    },
    statusInfo: {
        id: 'tariff.statusInfo',
        defaultMessage: 'Status Information'
    },
    generatedInfo: {
        id: 'tariff.generatedInfo',
        defaultMessage: 'Generated Information'
    },
    activeStatus: {
        id: 'tariff.activeStatus',
        defaultMessage: 'Active Status'
    },
    active: {
        id: 'tariff.active',
        defaultMessage: 'Active'
    },
    inactive: {
        id: 'tariff.inactive',
        defaultMessage: 'Inactive'
    }
});

class TariffEdit extends React.Component {
    static propTypes = {
        intl: PropTypes.object.isRequired,
        location: PropTypes.object,
        params: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.typeName = 'tariffs';
    }

    onSuccess = () => {
        const { params } = this.props;
        let path = urlMapping.getUrl(types['tariffs'], "list", { id: params.organisationId });
        this.context.router.push(path);
    };

    onFailure = (transaction) => {
        // Try to extract GraphQL error message
        let errorMessage = 'Failed to save tariff';

        try {
            const error = transaction.getError();
            if (error && error.res && error.res.payload && error.res.payload.errors) {
                const graphqlError = error.res.payload.errors[0];
                if (graphqlError && graphqlError.message) {
                    errorMessage = graphqlError.message;
                }
            }
        } catch (e) {
            // Ignore error parsing errors
        }

        displayError(errorMessage);
    };

    isTariffActive(tariff) {
        if (!tariff || !tariff.startDate) {
            return false;
        }

        const now = moment();
        const startDate = moment(tariff.startDate);
        const endDate = tariff.endDate ? moment(tariff.endDate) : null;

        if (now.isBefore(startDate)) return false;
        if (endDate && now.isAfter(endDate)) return false;
        return true;
    }

    generateCustomerNumber(organisationName) {
        // Generate 6-digit customer number from organisation name
        // This is a simple implementation - in production, this would be done on the backend
        const hash = organisationName.split('').reduce((acc, char) => {
            return acc + char.charCodeAt(0);
        }, 0);
        return String(hash % 1000000).padStart(6, '0');
    }

    generateTariffNumber(customerNumber, orderNumber) {
        return `${customerNumber}-${orderNumber}`;
    }

    render() {
        const isCreate = !this.props.params.id;
        const isView = this.props.location && this.props.location.pathname.includes('/view/');
        const data = isCreate ? {} : this.props.user.organisation.tariff;
        const organisation = this.props.user.organisation;
        const type = types[this.typeName];

        // Set default values for create
        if (isCreate) {
            data.organisation_id = organisation.id;
            data.startDate = moment().format('YYYY-MM-DD');

            // Generate customer number and tariff number
            data.customerNumber = this.generateCustomerNumber(organisation.name);
            data.orderNumber = (organisation.tariffs?.edges?.length || 0) + 1;
            data.tariffNumber = this.generateTariffNumber(data.customerNumber, data.orderNumber);
        }

        const isActive = isCreate ? true : this.isTariffActive(data);

        const columns = [
            'tariffType',
            'startDate',
            'endDate',
            'customerNumber',
            'tariffNumber',
            'orderNumber'
        ];

        const tariffDetailsColumns = {
            tariffType: {
                required: true,
                readOnly: !isCreate, // Read-only when editing existing tariff
                type: 'enum',
                values: TariffTypes
            },
            startDate: {
                required: true,
                readOnly: !isCreate, // Read-only when editing existing tariff
                type: 'date'
            },
            endDate: {
                type: 'date',
                description: 'Leave empty for indefinite validity'
            }
        };

        const generatedColumns = {
            customerNumber: {
                disabled: true,
                description: 'Generated from organisation name'
            },
            tariffNumber: {
                disabled: true,
                description: 'Generated from customer number and order'
            },
            orderNumber: {
                disabled: true,
                description: 'Sequential number for this organisation'
            }
        };

        const fatQuery = isCreate ?
            Relay.QL`fragment on CreateTariffPayload {
                edge {
                    node {
                        id,
                        realId,
                        tariffType,
                        startDate,
                        endDate,
                        active,
                        customerNumber,
                        tariffNumber,
                        orderNumber,
                        createdAt,
                        updatedAt
                    }
                },
                user {
                    id,
                    realId,
                    organisations(first: 100) {
                        edges {
                            node {
                                id,
                                realId,
                                tariffs(first: 100) {
                                    edges {
                                        node {
                                            id,
                                            realId,
                                            tariffType,
                                            startDate,
                                            endDate,
                                            active,
                                            customerNumber,
                                            tariffNumber,
                                            orderNumber,
                                            createdAt,
                                            updatedAt
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }` :
            Relay.QL`fragment on UpdateTariffPayload {
                updatedNode {
                    id,
                    realId,
                    tariffType,
                    startDate,
                    endDate,
                    active,
                    customerNumber,
                    tariffNumber,
                    orderNumber,
                    createdAt,
                    updatedAt
                }
            }`;

        const mutation = isCreate ?
            Relay.QL`mutation { createTariff }` :
            Relay.QL`mutation { updateTariff }`;

        return (
            <AppPanel
                breadcrumbParams={[
                    {type: types['organisations'], data: organisation},
                    {type: types['tariffs'], data: data}
                ]}
                title={isCreate ? this.props.intl.formatMessage(messages.titleAdd) :
                       (isView ? this.props.intl.formatMessage(messages.titleView) : this.props.intl.formatMessage(messages.title))}
            >
                {isView ? (
                    <div>
                        <div className="bs-callout bs-callout-info">
                            <h4><FormattedMessage {...messages.tariffDetails} /></h4>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Tariff Type</label>
                                        <div className="form-control-static">
                                            {TariffTypes[data.tariffType] ? TariffTypes[data.tariffType].name : data.tariffType}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Start Date</label>
                                        <div className="form-control-static">
                                            {data.startDate}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>End Date</label>
                                        <div className="form-control-static">
                                            {data.endDate || 'No end date'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bs-callout bs-callout-success">
                            <h4><FormattedMessage {...messages.statusInfo} /></h4>
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label><FormattedMessage {...messages.activeStatus} /></label>
                                        <div className="form-control-static">
                                            <span className={`label ${isActive ? 'label-success' : 'label-danger'}`}>
                                                <FormattedMessage {...messages[isActive ? 'active' : 'inactive']} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bs-callout bs-callout-default">
                            <h4><FormattedMessage {...messages.generatedInfo} /></h4>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label>Customer Number</label>
                                        <div className="form-control-static">
                                            {data.customerNumber}
                                        </div>
                                        <small className="help-block">Generated from organisation name</small>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label>Tariff Number</label>
                                        <div className="form-control-static">
                                            {data.tariffNumber}
                                        </div>
                                        <small className="help-block">Generated from customer number and order</small>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label>Order Number</label>
                                        <div className="form-control-static">
                                            {data.orderNumber}
                                        </div>
                                        <small className="help-block">Sequential number for this organisation</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center" style={{ marginTop: '20px' }}>
                            <Button
                                onClick={() => {
                                    const path = urlMapping.getUrl(types['tariffs'], "list", { id: this.props.params.organisationId });
                                    this.context.router.push(path);
                                }}
                            >
                                <i className="fa fa-arrow-left"></i> <FormattedMessage defaultMessage="Back to Tariff List" id="tariffs.backToList" />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Editor
                        cancelable
                        columns={columns}
                        data={data}
                        disableAutoFields
                        fatQuery={fatQuery}
                        focus="tariffType"
                        isCreate={isCreate}
                        metadata={type}
                        mutation={mutation}
                        onSuccess={this.onSuccess}
                        onFailure={this.onFailure}
                        organisationId={organisation.realId}
                        urlMapping={urlMapping}
                    >
                        <div className="bs-callout bs-callout-info">
                            <h4><FormattedMessage {...messages.tariffDetails} /></h4>
                            {isCreate ? (
                                <FormFields
                                    columns={tariffDetailsColumns}
                                    data={data}
                                    isCreate={isCreate}
                                    metadata={type}
                                />
                            ) : (
                                <div>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Tariff Type</label>
                                                <div className="form-control-static">
                                                    {TariffTypes[data.tariffType] ? TariffTypes[data.tariffType].name : data.tariffType}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Start Date</label>
                                                <div className="form-control-static">
                                                    {data.startDate}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <FormFields
                                                    columns={{
                                                        endDate: {
                                                            type: 'date',
                                                            description: 'Leave empty for indefinite validity'
                                                        }
                                                    }}
                                                    data={data}
                                                    isCreate={isCreate}
                                                    metadata={type}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bs-callout bs-callout-success">
                            <h4><FormattedMessage {...messages.statusInfo} /></h4>

                            <div className="row">
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label><FormattedMessage {...messages.activeStatus} /></label>
                                        <div className="form-control-static">
                                            <span className={`label ${isActive ? 'label-success' : 'label-danger'}`}>
                                                <FormattedMessage {...messages[isActive ? 'active' : 'inactive']} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="bs-callout bs-callout-default">
                            <h4><FormattedMessage {...messages.generatedInfo} /></h4>
                            {isCreate ? (
                                <FormFields
                                    columns={generatedColumns}
                                    data={data}
                                    isCreate={isCreate}
                                    metadata={type}
                                />
                            ) : (
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label>Customer Number</label>
                                            <div className="form-control-static">
                                                {data.customerNumber}
                                            </div>
                                            <small className="help-block">Generated from organisation name</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label>Tariff Number</label>
                                            <div className="form-control-static">
                                                {data.tariffNumber}
                                            </div>
                                            <small className="help-block">Generated from customer number and order</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label>Order Number</label>
                                            <div className="form-control-static">
                                                {data.orderNumber}
                                            </div>
                                            <small className="help-block">Sequential number for this organisation</small>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Editor>
                )}
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(TariffEdit), (props) => {
    // For create mode, just check organization exists
    if (!props.params.id) {
        return props.user && props.user.organisation;
    }
    // For edit mode, check that tariff data exists
    return props.user && props.user.organisation && props.user.organisation.tariff;
}), {
    initialVariables: {
        organisationId: '',
        id: '',
        skipTariff: false
    },
    prepareVariables(variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        // React Router maps :id parameter to variables.id
        if (variables.id) {
            variables.skipTariff = false;
        } else {
            variables.skipTariff = true;
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                id,
                realId,
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    tariff(id: $id) @skip(if: $skipTariff) {
                        id,
                        realId,
                        tariffType,
                        startDate,
                        endDate,
                        active,
                        customerNumber,
                        tariffNumber,
                        orderNumber,
                        createdAt,
                        updatedAt
                    },
                    tariffs(first: 100) {
                        edges {
                            node {
                                id,
                                realId,
                                tariffType,
                                startDate,
                                endDate,
                                active,
                                customerNumber,
                                tariffNumber,
                                orderNumber,
                                createdAt,
                                updatedAt
                            }
                        }
                    }
                }
            }
        `,
    },
});
